import React, { useMemo, useRef } from 'react'
import { TinsightParam } from '../../shared/types/computedKeyParam.type';
import { ISpeedAvgBest } from '../../shared/types/Stabledata.type';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { Box, Card, CardContent, Chip, Container, Divider, Typography } from '@mui/material';
import palette from '../../theme/palette';
import { t } from 'i18next';
import StatCard from '../../components/dashboard/StatCard';
import Highcharts from 'highcharts';
import { isEmpty } from 'lodash';
import { Icon } from '@iconify/react';

function SpeedAvgBest({ data, insightParams }: { data: ISpeedAvgBest; insightParams: TinsightParam[] }) {

    const chartRef = useRef<HighchartsReactRefObject | null>(null);

    const TIME_BEST_1000_KEY = 'insight.time_best_1000';
    const TIME_BEST_600_KEY = 'insight.time_best_600';
    const TIME_BEST_400_KEY = 'insight.time_best_400';
    const TIME_BEST_200_KEY = 'insight.time_best_200';


    const formattedAvg = data.averages.map(avg => ({
        ...avg,
        ...(insightParams.find(param => param.name === avg.parameter_name) || {}),
    }));



    const getParam = (param: string) => {
        return data.horse_best_records.find(
            br => br.parameter_value.parameter_name === param
        );
    }

    const getHorsesNames = useMemo(() => {
        if (isEmpty(data.horses_averages)) return [];
        return data.horses_averages.map(avg => avg?.horse?.name || '');
    }, [data.horses_averages]);


    const getBest1000 = () => {
        let avgs = data?.horses_averages || []
        if (isEmpty(avgs)) return []


        return data.horses_averages.map(horse => horse.averages.find(e => e?.parameter_name === TIME_BEST_1000_KEY)?.raw_numeric_value || 0)

    }

    const getBest600 = () => {
        let avgs = data?.horses_averages || []
        if (isEmpty(avgs)) return []


        return data.horses_averages.map(horse => horse.averages.find(e => e?.parameter_name === TIME_BEST_600_KEY)?.raw_numeric_value || 0)

    }

    const getBest400 = () => {
        let avgs = data?.horses_averages || []
        if (isEmpty(avgs)) return []


        return data.horses_averages.map(horse => horse.averages.find(e => e?.parameter_name === TIME_BEST_400_KEY)?.raw_numeric_value || 0)

    }

    const getBest200 = () => {
        let avgs = data?.horses_averages || []
        if (isEmpty(avgs)) return []


        return data.horses_averages.map(horse => horse.averages.find(e => e?.parameter_name === TIME_BEST_200_KEY)?.raw_numeric_value || 0)

    }

    const options: Highcharts.Options = {
        chart: {
            type: 'area',
            height: 450,
            // spacingTop: 80,
            animation: false,
            width: 1170,
            backgroundColor: 'transparent',
            style: {
                overflow: 'visible',
            },
        },
        title: {
            text: t('stable:title:AvgSpeedChartTitle'),
            align: 'left',
            style: {
                fontSize: "14px"
            }
        },
        plotOptions: {
            series: {
                pointPlacement: 'on',
                pointRange: 1,
                states: {
                    hover: {
                        enabled: false,
                    },
                    inactive: {
                        enabled: false,
                        opacity: 1,
                    },
                },
                marker: {
                    symbol: 'circle',
                },
            },
            area: {
                fillOpacity: 0.3,
                lineWidth: 2,
                marker: {
                    symbol: 'circle',
                },
                dataLabels: {
                    allowOverlap: true,
                    enabled: true,
                    useHTML: true,
                    inside: true,
                    align: 'center',
                    formatter: function () {
                        const isEven = this.point.index % 2 === 0;
                        const yOffset = isEven ? 24 : 0; // move label up or down


                        return `<div style="
                    position: relative;
                    transform: translateY(${yOffset}px);
                    color: var(--lightGray);
                    font-size: 10px;
                    font-weight: 400;
                    ">
                    ${this.y?.toFixed(2)}
                </div>`;
                    }
                }
            },
        },
        credits: {
            enabled: false,
        },
        xAxis: {
            categories: getHorsesNames,
            gridLineWidth: 0,
            endOnTick: false,

            maxPadding: 0.15,
            showLastLabel: true,
            labels: {
                allowOverlap: true,
                useHTML: true,
                overflow: 'allow',

                rotation: -80,

                formatter: function () {
                    const total = this.axis.categories?.length ?? 0;
                    let xOffset = 0;
                    if (this.pos === 0) xOffset = 20;           // first label
                    else if (this.pos === total - 1) xOffset = -20; // last label

                    // Highcharts SVG label x offset
                    (this as any).x += xOffset;

                    return this.value as string;
                },
                style: {
                    fontSize: '10px'
                },
                // Force show all labels
                step: 1,
            },
            // Ensure enough space for the last label
            tickmarkPlacement: 'on',
        },
        legend: {
            enabled: true,
            layout: 'horizontal',
            align: 'right',         // Align to right side
            verticalAlign: 'top',   // Align to top
            y: 0,                  // Space from top edge (positive = push downward)
            floating: true,        // Set to true if you want it over the plot area
            itemMarginTop: 5,       // Space between legend items (optional)
            itemMarginBottom: 5,
            padding: 8,             // Padding inside the legend box
            symbolPadding: 8        // Space between symbol and text
        },
        yAxis: {
            gridLineWidth: 0,// disables horizontal grid lines
            title: {
                text: ''
            }
        },
        tooltip: {
            enabled: false,
        },
        series: [
            {
                name: t(formattedAvg.find(e => e.parameter_name === TIME_BEST_600_KEY)?.short_name_translated || "").concat(` (${t('firstSection:average')})`),
                data: getBest600(),
                type: 'area',
                color: 'rgba(65,105,225,.5)',
                zIndex: 1,
            },
            {
                name: t(formattedAvg.find(e => e.parameter_name === TIME_BEST_400_KEY)?.short_name_translated || "").concat(` (${t('firstSection:average')})`),
                data: getBest400(),
                type: 'area',
                color: 'rgba(65,105,225,.7)',
                zIndex: 2,
            },
            {
                name: t(formattedAvg.find(e => e.parameter_name === TIME_BEST_200_KEY)?.short_name_translated || "").concat(` (${t('firstSection:average')})`),
                data: getBest200(),
                color: 'rgba(65,105,225,1)',
                type: 'area',
                zIndex: 3,
            },

        ],
    };
    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                <Typography variant="h4" sx={{ color: palette.light.primary.main }}>
                    {t('stable:title:'.concat(data.type))}
                </Typography>
                <img
                    style={{ objectFit: 'contain', width: 55, height: 55, marginRight: 15 }}
                    src="/arioneo_logo_white.png"
                    alt="arioneo logo"
                />
            </Box>
            <Card sx={{ mb: 2 }} >

                <CardContent >
                    <Typography variant='h5'>{t('dataParameters:groups:averages')}</Typography>
                    <Box display="flex" justifyContent="space-evenly" alignItems="center" gap={2} mt={3}>
                        {formattedAvg.map(avg => {
                            const found = getParam(avg.parameter_name)

                            return (
                                <StatCard
                                    key={avg.parameter_name}
                                    icon={avg.icon}
                                    title={avg.name_translated || ''}
                                    value={avg.formatted_value.concat(` ${avg.unit_translated}`)}
                                    isLoading={false}
                                >
                                    {found ? (
                                        <Box display="flex" alignItems={'center'} gap={.5} >
                                            <Icon  width={20} height={20} icon={'fluent-color:trophy-16'} />
                                            <Divider flexItem orientation='vertical'/>
                                            <Box display={'flex'} flexDirection={'column'} alignItems={'self-start'}>
                                                <Typography color="#808080" variant="subtitle2" fontSize="15px">
                                                    {found.parameter_value.formatted_value}
                                                </Typography>
                                                <Typography variant="caption" fontSize="13px">
                                                    {found.horse?.name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : null}
                                </StatCard>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>
            <Typography variant='caption' color={'#808080'}>{t('stable:AvgSpeedExplenation')}</Typography>
            <div id="chart-container" style={{ marginTop: 2 }}>
                <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
            </div>
        </>
    )
}

export default SpeedAvgBest